'use client'
import s from './Hero.module.scss'
import { useContext, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import LazyImage from '@/components/common/LazyImage'
import { Image } from '@/types/common/ImageData'
import { WebContext } from '@/context/webContext'
import LazyVideo from '@/components/common/LazyVideo'
import { Body } from '@/types/common/Body'
import { PortableText } from 'next-sanity'
import { portableBlockComponents } from '@/utils/portableText'
import { usePathname } from 'next/navigation'

interface ImageProp {
    image: Image | null;
}

interface Hero {
    image: ImageProp | null;
    imageMobile: ImageProp | null;
    video: {
        videoUrl: string | null;
        videoMobileUrl: string | null;
    };
    mediaType: string;
    subtitle: string;
    headerColor: string | null;
    title: Body[] | null;
    link: string | null;
}

interface IntroductionProps {
    hero: Hero;
}

export default function HeroComponent({
    hero,
}: IntroductionProps) {

    const {
        setColorHeader,
        setBackgroundHeader,
    } = useContext(WebContext)

    const [isMobile, setIsMobile] = useState(false);

    // Detectar si es mobile o desktop
    useEffect(() => {
        const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    const pathname = usePathname();
    const isHome = pathname === "/";
    const [firstTimeInHome, setFirstTimeInHome] = useState(isHome);

    const heroRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setColorHeader(hero.headerColor);
        setBackgroundHeader(false);
    }, []);

    useEffect(() => {
        setColorHeader(hero.headerColor);
        setBackgroundHeader(false);

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // El Hero está visible
                    setColorHeader(hero.headerColor);
                    setBackgroundHeader(false);
                } else {
                    // El Hero ya no está visible
                    setBackgroundHeader(true);
                    setColorHeader('black');
                }
            },
            { threshold: 0.1 } // Umbral para considerar el Hero visible
        );

        if (heroRef.current) {
            observer.observe(heroRef.current);
        }

        return () => {
            if (heroRef.current) {
                observer.unobserve(heroRef.current);
            }
        };
    }, [hero, setColorHeader, setBackgroundHeader]);

    return (
        <section className={s.heroComponent} id="hero">

            {<div ref={heroRef} className={s.hero}>
                <div className={s.heroMedia}>
                    {hero.mediaType === 'video' ? (
                        // VIDEO BLOCK
                        isMobile && hero.video?.videoMobileUrl ? (
                            <LazyVideo
                                src={hero.video.videoMobileUrl}
                                autoplay={true}
                                controls={false}
                                defaultInView={true}
                                alt={hero.subtitle || ""}
                            />
                        ) : hero.video?.videoUrl ? (
                            <LazyVideo
                                src={hero.video.videoUrl}
                                autoplay={true}
                                controls={false}
                                defaultInView={true}
                                alt={hero.subtitle || ""}
                            />
                        ) : null
                    ) : hero.mediaType === 'image' ? (
                        // IMAGE BLOCK
                        isMobile && hero.imageMobile?.image?.imageUrl ? (
                            <LazyImage
                                src={hero.imageMobile.image.imageUrl}
                                width={0}
                                height={0}
                                blurDataURL={hero.imageMobile.image.ref}
                                fill
                                alt={hero.imageMobile.image.attribution || ""}
                                caption={hero.imageMobile.image.caption || null}
                                filename={hero.imageMobile.image.filename || ""}
                            />
                        ) : hero.image?.image?.imageUrl ? (
                            <LazyImage
                                src={hero.image.image.imageUrl}
                                width={0}
                                height={0}
                                blurDataURL={hero.image.image.ref}
                                fill
                                alt={hero.image.image.attribution || ""}
                                caption={hero.image.image.caption || null}
                                filename={hero.image.image.filename || ""}
                            />
                        ) : null
                    ) : null}

                </div>
                <motion.div
                    className={s.heroContent}
                    initial={{ opacity: firstTimeInHome ? 0 : 1, y: firstTimeInHome ? 50 : 0 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut', delay: 0.6 }}
                    viewport={{ once: true }}
                >
                    {hero.link ? <Link href={hero.link || ''} passHref>

                        <div className={s.heroText}>
                            <p>{hero.subtitle}</p>
                            {hero.title &&
                                <PortableText
                                    value={hero.title}
                                    components={portableBlockComponents(hero.title)}
                                />
                            }
                        </div>
                    </Link> : <div className={s.heroText}>
                        <p>{hero.subtitle}</p>
                        {hero.title &&
                            <PortableText
                                value={hero.title}
                                components={portableBlockComponents(hero.title)}
                            />
                        }
                    </div>
                    }
                </motion.div>
            </div>
            }
        </section>
    )
}
